import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, DollarSignIcon, BuildingIcon, CalendarIcon, FileTextIcon, CheckCircleIcon } from 'lucide-react';
export function BillFinancingWizard() {
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    loanPackage: '',
    financingAmount: '',
    requestedTerm: '30',
    purpose: '',
    additionalNotes: ''
  });
  // Mock bill data
  const bill = {
    id: id,
    billNumber: 'BILL-2024-002',
    supplierName: 'Spice Traders Co',
    amount: 87500,
    dueDate: '2024-02-20'
  };
  const dfccLoanPackages = [{
    id: 'standard',
    name: 'Standard Financing',
    interestRate: '12%',
    maxTerm: '90 days',
    processingTime: '48 hours',
    description: 'Ideal for regular business operations with flexible terms'
  }, {
    id: 'premium',
    name: 'Premium Financing',
    interestRate: '10.5%',
    maxTerm: '120 days',
    processingTime: '24 hours',
    description: 'Lower rates for established businesses with priority processing'
  }, {
    id: 'express',
    name: 'Express Financing',
    interestRate: '14%',
    maxTerm: '60 days',
    processingTime: '12 hours',
    description: 'Quick approval for urgent cash flow needs'
  }, {
    id: 'sme',
    name: 'SME Growth Package',
    interestRate: '11%',
    maxTerm: '90 days',
    processingTime: '36 hours',
    description: 'Special rates for small and medium enterprises'
  }];
  const handleSubmit = () => {
    console.log('Submitting financing request:', {
      billId: id,
      ...formData
    });
    navigate('/bills');
  };
  const selectedPackage = dfccLoanPackages.find(p => p.id === formData.loanPackage);
  return <div className="max-w-4xl mx-auto space-y-6" data-id="element-5339">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-5340">
        <button onClick={() => navigate(`/bill-detail/${id}`)} className="flex items-center gap-2 text-gray-600 hover:text-gray-900" data-id="element-5341">
          <ArrowLeftIcon size={20} data-id="element-5342" />
          Back to Bill
        </button>
      </div>
      {/* Progress Steps */}
      <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-5343">
        <div className="flex items-center justify-between mb-8" data-id="element-5344">
          <div className="flex-1" data-id="element-5345">
            <div className={`flex items-center ${step >= 1 ? 'text-blue-600' : 'text-gray-400'}`} data-id="element-5346">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`} data-id="element-5347">
                1
              </div>
              <div className="ml-3" data-id="element-5348">
                <div className="text-sm font-medium" data-id="element-5349">Bill Details</div>
              </div>
            </div>
          </div>
          <div className={`flex-1 h-1 ${step >= 2 ? 'bg-blue-600' : 'bg-gray-200'}`} data-id="element-5350"></div>
          <div className="flex-1" data-id="element-5351">
            <div className={`flex items-center ${step >= 2 ? 'text-blue-600' : 'text-gray-400'}`} data-id="element-5352">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`} data-id="element-5353">
                2
              </div>
              <div className="ml-3" data-id="element-5354">
                <div className="text-sm font-medium" data-id="element-5355">Select Provider</div>
              </div>
            </div>
          </div>
          <div className={`flex-1 h-1 ${step >= 3 ? 'bg-blue-600' : 'bg-gray-200'}`} data-id="element-5356"></div>
          <div className="flex-1" data-id="element-5357">
            <div className={`flex items-center ${step >= 3 ? 'text-blue-600' : 'text-gray-400'}`} data-id="element-5358">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 3 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`} data-id="element-5359">
                3
              </div>
              <div className="ml-3" data-id="element-5360">
                <div className="text-sm font-medium" data-id="element-5361">Financing Terms</div>
              </div>
            </div>
          </div>
          <div className={`flex-1 h-1 ${step >= 4 ? 'bg-blue-600' : 'bg-gray-200'}`} data-id="element-5362"></div>
          <div className="flex-1" data-id="element-5363">
            <div className={`flex items-center ${step >= 4 ? 'text-blue-600' : 'text-gray-400'}`} data-id="element-5364">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 4 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`} data-id="element-5365">
                4
              </div>
              <div className="ml-3" data-id="element-5366">
                <div className="text-sm font-medium" data-id="element-5367">Review & Submit</div>
              </div>
            </div>
          </div>
        </div>
        {/* Step 1: Bill Details */}
        {step === 1 && <div className="space-y-6" data-id="element-5368">
            <div data-id="element-5369">
              <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-5370">
                Bill Information
              </h2>
              <p className="text-gray-600" data-id="element-5371">
                Review the bill details for financing
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-6 space-y-4" data-id="element-5372">
              <div className="grid grid-cols-2 gap-4" data-id="element-5373">
                <div data-id="element-5374">
                  <div className="text-sm text-gray-500" data-id="element-5375">Bill Number</div>
                  <div className="text-base font-medium text-gray-900" data-id="element-5376">
                    {bill.billNumber}
                  </div>
                </div>
                <div data-id="element-5377">
                  <div className="text-sm text-gray-500" data-id="element-5378">Supplier</div>
                  <div className="text-base font-medium text-gray-900" data-id="element-5379">
                    {bill.supplierName}
                  </div>
                </div>
                <div data-id="element-5380">
                  <div className="text-sm text-gray-500" data-id="element-5381">Due Date</div>
                  <div className="text-base font-medium text-gray-900" data-id="element-5382">
                    {new Date(bill.dueDate).toLocaleDateString('en-GB')}
                  </div>
                </div>
                <div data-id="element-5383">
                  <div className="text-sm text-gray-500" data-id="element-5384">Bill Amount</div>
                  <div className="text-2xl font-bold text-gray-900" data-id="element-5385">
                    Rs. {bill.amount.toLocaleString('en-LK')}
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-5386">
              <div className="flex items-start gap-3" data-id="element-5387">
                <DollarSignIcon size={20} className="text-blue-600 mt-0.5" data-id="element-5388" />
                <div className="text-sm text-blue-800" data-id="element-5389">
                  <div className="font-medium mb-1" data-id="element-5390">
                    Bill Financing Benefits
                  </div>
                  <ul className="list-disc list-inside space-y-1" data-id="element-5391">
                    <li data-id="element-5392">Improve cash flow by paying suppliers early</li>
                    <li data-id="element-5393">Maintain good supplier relationships</li>
                    <li data-id="element-5394">Take advantage of early payment discounts</li>
                    <li data-id="element-5395">Flexible repayment terms</li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="flex justify-end" data-id="element-5396">
              <button onClick={() => setStep(2)} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700" data-id="element-5397">
                Continue
              </button>
            </div>
          </div>}
        {/* Step 2: Select Provider */}
        {step === 2 && <div className="space-y-6" data-id="element-5398">
            <div data-id="element-5399">
              <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-5400">
                Select Rabbit Gateway Financing Package
              </h2>
              <p className="text-gray-600" data-id="element-5401">
                Choose a financing package that best suits your needs
              </p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6" data-id="element-5402">
              <div className="flex items-start gap-3" data-id="element-5403">
                <BuildingIcon size={20} className="text-blue-600 mt-0.5" data-id="element-5404" />
                <div data-id="element-5405">
                  <div className="font-semibold text-blue-900 mb-1" data-id="element-5406">
                    Rabbit Gateway - Your Trusted Financial Partner
                  </div>
                  <div className="text-sm text-blue-800" data-id="element-5407">
                    As a valued Rabbit Gateway customer, you have access to
                    exclusive financing packages tailored for your business
                    needs.
                  </div>
                </div>
              </div>
            </div>
            <div className="space-y-4" data-id="element-5408">
              {dfccLoanPackages.map(pkg => <label key={pkg.id} className={`block p-6 border-2 rounded-lg cursor-pointer transition-all ${formData.loanPackage === pkg.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'}`} data-id="element-5409">
                  <input type="radio" name="loanPackage" value={pkg.id} checked={formData.loanPackage === pkg.id} onChange={e => setFormData({
              ...formData,
              loanPackage: e.target.value
            })} className="sr-only" data-id="element-5410" />
                  <div className="flex items-start justify-between" data-id="element-5411">
                    <div className="flex-1" data-id="element-5412">
                      <div className="flex items-center gap-2 mb-2" data-id="element-5413">
                        <div className="text-lg font-semibold text-gray-900" data-id="element-5414">
                          {pkg.name}
                        </div>
                        {formData.loanPackage === pkg.id && <CheckCircleIcon size={20} className="text-blue-600" data-id="element-5415" />}
                      </div>
                      <p className="text-sm text-gray-600 mb-4" data-id="element-5416">
                        {pkg.description}
                      </p>
                      <div className="grid grid-cols-3 gap-4 text-sm" data-id="element-5417">
                        <div data-id="element-5418">
                          <div className="text-gray-500" data-id="element-5419">Interest Rate</div>
                          <div className="font-medium text-gray-900" data-id="element-5420">
                            {pkg.interestRate}
                          </div>
                        </div>
                        <div data-id="element-5421">
                          <div className="text-gray-500" data-id="element-5422">Max Term</div>
                          <div className="font-medium text-gray-900" data-id="element-5423">
                            {pkg.maxTerm}
                          </div>
                        </div>
                        <div data-id="element-5424">
                          <div className="text-gray-500" data-id="element-5425">Processing Time</div>
                          <div className="font-medium text-gray-900" data-id="element-5426">
                            {pkg.processingTime}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </label>)}
            </div>
            <div className="flex justify-between" data-id="element-5427">
              <button onClick={() => setStep(1)} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-5428">
                Back
              </button>
              <button onClick={() => setStep(3)} disabled={!formData.loanPackage} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-5429">
                Continue
              </button>
            </div>
          </div>}
        {/* Step 3: Financing Terms */}
        {step === 3 && <div className="space-y-6" data-id="element-5430">
            <div data-id="element-5431">
              <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-5432">
                Financing Terms
              </h2>
              <p className="text-gray-600" data-id="element-5433">
                Specify the financing amount and terms
              </p>
            </div>
            <div className="space-y-6" data-id="element-5434">
              <div data-id="element-5435">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-5436">
                  Financing Amount (THB) *
                </label>
                <input type="number" value={formData.financingAmount} onChange={e => setFormData({
              ...formData,
              financingAmount: e.target.value
            })} placeholder="Enter amount" max={bill.amount} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-5437" />
                <div className="mt-1 text-sm text-gray-500" data-id="element-5438">
                  Maximum: ฿ {bill.amount.toLocaleString('en-TH')}
                </div>
              </div>
              <div data-id="element-5439">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-5440">
                  Requested Term (Days) *
                </label>
                <select value={formData.requestedTerm} onChange={e => setFormData({
              ...formData,
              requestedTerm: e.target.value
            })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-5441">
                  <option value="15" data-id="element-5442">15 days</option>
                  <option value="30" data-id="element-5443">30 days</option>
                  <option value="45" data-id="element-5444">45 days</option>
                  <option value="60" data-id="element-5445">60 days</option>
                  <option value="90" data-id="element-5446">90 days</option>
                </select>
              </div>
              <div data-id="element-5447">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-5448">
                  Purpose of Financing *
                </label>
                <select value={formData.purpose} onChange={e => setFormData({
              ...formData,
              purpose: e.target.value
            })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-5449">
                  <option value="" data-id="element-5450">Select purpose</option>
                  <option value="early_payment" data-id="element-5451">Early Payment Discount</option>
                  <option value="cash_flow" data-id="element-5452">Cash Flow Management</option>
                  <option value="supplier_relationship" data-id="element-5453">
                    Supplier Relationship
                  </option>
                  <option value="other" data-id="element-5454">Other</option>
                </select>
              </div>
              <div data-id="element-5455">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-5456">
                  Additional Notes
                </label>
                <textarea value={formData.additionalNotes} onChange={e => setFormData({
              ...formData,
              additionalNotes: e.target.value
            })} rows={4} placeholder="Any additional information..." className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-5457" />
              </div>
            </div>
            <div className="flex justify-between" data-id="element-5458">
              <button onClick={() => setStep(2)} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-5459">
                Back
              </button>
              <button onClick={() => setStep(4)} disabled={!formData.financingAmount || !formData.requestedTerm || !formData.purpose} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-5460">
                Review Request
              </button>
            </div>
          </div>}
        {/* Step 4: Review & Submit */}
        {step === 4 && <div className="space-y-6" data-id="element-5461">
            <div data-id="element-5462">
              <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-5463">
                Review Financing Request
              </h2>
              <p className="text-gray-600" data-id="element-5464">
                Please review your financing request before submitting
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-6 space-y-6" data-id="element-5465">
              <div data-id="element-5466">
                <h3 className="text-sm font-medium text-gray-500 mb-3" data-id="element-5467">
                  BILL INFORMATION
                </h3>
                <div className="grid grid-cols-2 gap-4" data-id="element-5468">
                  <div data-id="element-5469">
                    <div className="text-sm text-gray-500" data-id="element-5470">Bill Number</div>
                    <div className="text-base font-medium text-gray-900" data-id="element-5471">
                      {bill.billNumber}
                    </div>
                  </div>
                  <div data-id="element-5472">
                    <div className="text-sm text-gray-500" data-id="element-5473">Supplier</div>
                    <div className="text-base font-medium text-gray-900" data-id="element-5474">
                      {bill.supplierName}
                    </div>
                  </div>
                </div>
              </div>
              <div className="pt-4 border-t border-gray-200" data-id="element-5475">
                <h3 className="text-sm font-medium text-gray-500 mb-3" data-id="element-5476">
                  FINANCING DETAILS
                </h3>
                <div className="grid grid-cols-2 gap-4" data-id="element-5477">
                  <div data-id="element-5478">
                    <div className="text-sm text-gray-500" data-id="element-5479">Bank</div>
                    <div className="text-base font-medium text-gray-900" data-id="element-5480">
                      Rabbit Gateway
                    </div>
                  </div>
                  <div data-id="element-5481">
                    <div className="text-sm text-gray-500" data-id="element-5482">Package</div>
                    <div className="text-base font-medium text-gray-900" data-id="element-5483">
                      {selectedPackage?.name}
                    </div>
                  </div>
                  <div data-id="element-5484">
                    <div className="text-sm text-gray-500" data-id="element-5485">Interest Rate</div>
                    <div className="text-base font-medium text-gray-900" data-id="element-5486">
                      {selectedPackage?.interestRate}
                    </div>
                  </div>
                  <div data-id="element-5487">
                    <div className="text-sm text-gray-500" data-id="element-5488">Processing Time</div>
                    <div className="text-base font-medium text-gray-900" data-id="element-5489">
                      {selectedPackage?.processingTime}
                    </div>
                  </div>
                  <div data-id="element-5490">
                    <div className="text-sm text-gray-500" data-id="element-5491">
                      Financing Amount
                    </div>
                    <div className="text-base font-medium text-gray-900" data-id="element-5492">
                      Rs.{' '}
                      {parseFloat(formData.financingAmount).toLocaleString('en-LK')}
                    </div>
                  </div>
                  <div data-id="element-5493">
                    <div className="text-sm text-gray-500" data-id="element-5494">Term</div>
                    <div className="text-base font-medium text-gray-900" data-id="element-5495">
                      {formData.requestedTerm} days
                    </div>
                  </div>
                  <div data-id="element-5496">
                    <div className="text-sm text-gray-500" data-id="element-5497">Purpose</div>
                    <div className="text-base font-medium text-gray-900 capitalize" data-id="element-5498">
                      {formData.purpose.replace('_', ' ')}
                    </div>
                  </div>
                </div>
              </div>
              {formData.additionalNotes && <div className="pt-4 border-t border-gray-200" data-id="element-5499">
                  <h3 className="text-sm font-medium text-gray-500 mb-2" data-id="element-5500">
                    ADDITIONAL NOTES
                  </h3>
                  <p className="text-sm text-gray-700" data-id="element-5501">
                    {formData.additionalNotes}
                  </p>
                </div>}
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-5502">
              <div className="flex items-start gap-3" data-id="element-5503">
                <FileTextIcon size={20} className="text-blue-600 mt-0.5" data-id="element-5504" />
                <div className="text-sm text-blue-800" data-id="element-5505">
                  By submitting this request, you agree to the financing terms
                  and conditions set by Rabbit Gateway for the{' '}
                  {selectedPackage?.name}. The request will be processed within{' '}
                  {selectedPackage?.processingTime}.
                </div>
              </div>
            </div>
            <div className="flex justify-between" data-id="element-5506">
              <button onClick={() => setStep(3)} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-5507">
                Back
              </button>
              <button onClick={handleSubmit} className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700" data-id="element-5508">
                Submit Financing Request
              </button>
            </div>
          </div>}
      </div>
    </div>;
}